import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { CONTEXT_DOC_TYPES, ContextDocType } from '@qtek/shared/models';

export interface AddContextDocDialogResult {
  file: File;
  docType: ContextDocType;
}

interface ContextDocTypeOption {
  docType: ContextDocType;
  labelKey: string;
  themeClass: string;
}

export const addContextDocDialogConfig: MatDialogConfig = {
  autoFocus: false,
  width: '600px',
};

const THEME_CLASS: Record<ContextDocType, string> = {
  credit_policy: 'type-card--credit-policy',
  term_sheet: 'type-card--term-sheet',
  credit_agreement: 'type-card--credit-agreement',
  market_report: 'type-card--market-report',
  survey_results: 'type-card--survey-results',
  other: 'type-card--other',
};

const TYPE_OPTIONS: ContextDocTypeOption[] = CONTEXT_DOC_TYPES.map(
  ({ docType, labelKey }) => ({
    docType,
    labelKey,
    themeClass: THEME_CLASS[docType],
  })
);

@Component({
  selector: 'qt-add-context-doc-dialog',
  templateUrl: './add-context-doc-dialog.component.html',
  styleUrls: ['./add-context-doc-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    MatDialogModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    TranslationCoreModule,
  ],
})
export class AddContextDocDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<AddContextDocDialogComponent, AddContextDocDialogResult>
  );

  @ViewChild('fileInput', { static: true })
  fileInputRef: ElementRef<HTMLInputElement>;

  readonly typeOptions = TYPE_OPTIONS;
  readonly isDragging = signal(false);
  readonly selectedType = signal<ContextDocType | null>(null);

  selectType(docType: ContextDocType): void {
    this.selectedType.set(docType);
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) this.close(file);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
    if (!this.selectedType()) return;

    const file = event.dataTransfer?.files?.[0];
    if (file) this.close(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.selectedType()) return;
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
  }

  private close(file: File): void {
    const docType = this.selectedType();
    if (!docType) return;

    this.dialogRef.close({ file, docType });
  }
}
